import React, { useEffect, useState } from 'react'
import { getDepositCandidates } from '../core/api'
import {
  DEPOSIT_GROUP_COLOR,
  DEPOSIT_GROUP_LABEL,
  DEPOSIT_STATUS_LABEL,
  V2_ACTION_LABEL,
  V2_PASS_LABEL,
  v2ReasonLabel,
} from '../core/consts'
import { duration, money, shortMsk } from '../core/format'
import { noPayoutReasonLabel } from '../core/reason'
import { TimelineCandidate, TimelineDecision, TimelineDeposit } from '../core/response.types'
import { DetailRow, DetailSection } from './Detail'

const WIDTH = 340
/** Выше этого карточка прокручивается внутри себя. */
const MAX_HEIGHT = 440

type Props = {
  deposits: TimelineDeposit[]
  merchantName: (id: number) => string
  methodName: (id: number) => string
  onCandidates: (candidates: TimelineCandidate[]) => void
  onClose: () => void
}

type LoadState = 'loading' | 'ready' | 'error' | 'none'

const depositLabel = (d: TimelineDeposit) =>
  d.payment_ticket_id !== null ? `#${d.payment_ticket_id}` : `пречек #${d.precheck_log_id}`

/**
 * Окошко по клику на точку. Группа сначала просит выбрать депозит. Выбранный
 * депозит показывается только после ответа по кандидатам — до него спиннер,
 * чтобы данные и подсветка выплат появились одновременно. У откинутого пречеком
 * подбора не было — запроса нет, данные сразу.
 */
const DepositPopover: React.FC<Props> = ({
  deposits,
  merchantName,
  methodName,
  onCandidates,
  onClose,
}) => {
  const isGroup = deposits.length > 1
  const [chosen, setChosen] = useState<TimelineDeposit | null>(isGroup ? null : deposits[0])
  const [state, setState] = useState<LoadState>('loading')
  const [count, setCount] = useState(0)
  const [decision, setDecision] = useState<TimelineDecision | null>(null)
  const [list, setList] = useState<TimelineCandidate[]>([])

  useEffect(() => {
    setChosen(isGroup ? null : deposits[0])
  }, [deposits, isGroup])

  useEffect(() => {
    onCandidates([])
    setDecision(null)
    setList([])
    if (!chosen) return
    if (chosen.payment_ticket_id === null) {
      setState('none')
      return
    }
    let cancelled = false
    setState('loading')
    getDepositCandidates(chosen.payment_ticket_id)
      .then((res) => {
        if (cancelled) return
        if (!res.data.success || !res.data.data) {
          setState('error')
          return
        }
        const got = res.data.data.candidates
        onCandidates(got)
        setCount(got.length)
        setList(got)
        setDecision(res.data.data.decision ?? null)
        setState('ready')
      })
      .catch(() => {
        if (!cancelled) setState('error')
      })
    return () => {
      cancelled = true
    }
  }, [chosen, onCandidates])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const header = (title: string) => (
    <div className='d-flex justify-content-between align-items-start mb-2'>
      <strong className='fs-6'>{title}</strong>
      <button
        type='button'
        className='btn btn-sm btn-icon btn-light ms-3'
        onClick={onClose}
        aria-label='Закрыть'
      >
        ×
      </button>
    </div>
  )

  return (
    <div
      className='card shadow p-3 fs-7'
      style={{ width: WIDTH, maxHeight: MAX_HEIGHT, overflowY: 'auto' }}
    >
      {!chosen && (
        <>
          {header(`${deposits.length} депозитов · ${deposits[0].amount.toLocaleString('ru-RU')}`)}
          <div className='text-muted fs-8 mb-2'>Выберите депозит</div>
          <div className='d-flex flex-column gap-1'>
            {deposits.map((d, i) => (
              <button
                key={`${d.payment_ticket_id ?? 'p'}-${d.precheck_log_id ?? i}`}
                type='button'
                className='btn btn-sm btn-light text-start d-flex align-items-center gap-2 py-2'
                onClick={() => setChosen(d)}
              >
                <span
                  className='rounded-circle d-inline-block flex-shrink-0'
                  style={{ width: 8, height: 8, background: DEPOSIT_GROUP_COLOR[d.group] }}
                />
                <span className='fw-semibold'>{depositLabel(d)}</span>
                <span className='text-muted'>{shortMsk(d.at)}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {chosen && state === 'loading' && (
        <>
          {header(chosen.amount.toLocaleString('ru-RU'))}
          <div className='d-flex align-items-center gap-2 text-muted py-4'>
            <span className='spinner-border spinner-border-sm' role='status' /> Загружаем данные по
            мэтчам…
          </div>
        </>
      )}

      {chosen && state !== 'loading' && (
        <>
          <div className='d-flex align-items-start justify-content-between gap-2'>
            <div>
              <div className='fs-5 fw-bold lh-1'>{money(chosen.amount)}</div>
              <div className='text-muted mt-1'>
                Депозит {depositLabel(chosen)} · {merchantName(chosen.merchant_id)}
              </div>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <span
                className='badge fw-semibold'
                style={{ background: DEPOSIT_GROUP_COLOR[chosen.group], color: '#FFFFFF' }}
              >
                {DEPOSIT_GROUP_LABEL[chosen.group]}
              </span>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                onClick={onClose}
                aria-label='Закрыть'
              >
                ×
              </button>
            </div>
          </div>

          <DetailSection title='Заявка'>
            <DetailRow label='Метод'>{methodName(chosen.payment_method_id)}</DetailRow>
            {chosen.status && (
              <DetailRow label='Статус'>
                {DEPOSIT_STATUS_LABEL[chosen.status] ?? chosen.status}
              </DetailRow>
            )}
            <DetailRow label='Токен'>{chosen.token ?? '—'}</DetailRow>
            <DetailRow label='Попытка'>{shortMsk(chosen.at)}</DetailRow>
            {chosen.completed_at && (
              <DetailRow label='Закрыт'>
                {shortMsk(chosen.completed_at)} · через{' '}
                {duration(Date.parse(chosen.completed_at) - Date.parse(chosen.at))}
              </DetailRow>
            )}
          </DetailSection>

          <DetailSection title='Суммы'>
            <DetailRow label='Запрошено'>
              {money(chosen.requested_amount ?? chosen.amount)}
            </DetailRow>
            {chosen.issued_amount != null && (
              <DetailRow label='Выдано к оплате'>{money(chosen.issued_amount)}</DetailRow>
            )}
            {chosen.paid_amount != null && (
              <DetailRow label='Зачислено'>{money(chosen.paid_amount)}</DetailRow>
            )}
          </DetailSection>

          {chosen.matched_payout_ticket_id != null && (
            <DetailSection title='Мэтч'>
              <DetailRow label='Выплата'>#{chosen.matched_payout_ticket_id}</DetailRow>
              <DetailRow label='Сумма чанка'>{money(chosen.matched_amount)}</DetailRow>
              {chosen.bank && <DetailRow label='Банк реквизита'>{chosen.bank}</DetailRow>}
            </DetailSection>
          )}

          {decision && (
            <DetailSection title='Решение второго контура'>
              <DetailRow label='Итог'>
                {V2_ACTION_LABEL[decision.action] ?? decision.action}
              </DetailRow>
              {decision.reason && (
                <DetailRow label='Причина'>{v2ReasonLabel(decision.reason)}</DetailRow>
              )}
              {decision.pass && (
                <DetailRow label='Проход'>
                  {V2_PASS_LABEL[decision.pass] ?? decision.pass}
                </DetailRow>
              )}
              <DetailRow label='Запрошено'>{money(decision.asked)}</DetailRow>
              {decision.billed != null && (
                <DetailRow label='Выставлено'>{money(decision.billed)}</DetailRow>
              )}
              {decision.tiles != null && decision.tiles > 0 && (
                <DetailRow label='Плиток'>{decision.tiles}</DetailRow>
              )}
              <DetailRow label='Выплат в пуле'>
                {list.filter((c) => !c.disqualified).length} взвешено ·{' '}
                {list.filter((c) => c.gate).length} не прошли правила ·{' '}
                {list.filter((c) => c.excluded).length} исключены
              </DetailRow>
            </DetailSection>
          )}

          <DetailSection title='Подбор'>
            {state === 'ready' && (
              <DetailRow label='Кандидатов'>
                {count > 0 ? `${count} — подсвечены на графике` : 'неизвестны'}
              </DetailRow>
            )}
            {chosen.available_payouts != null && (
              <DetailRow label='Было выплат в пуле'>{chosen.available_payouts}</DetailRow>
            )}
            {state === 'none' && <DetailRow label='Пречек'>{chosen.reason ?? 'отказ'}</DetailRow>}
            {noPayoutReasonLabel(chosen) && (
              <DetailRow label='Отказ'>{noPayoutReasonLabel(chosen)}</DetailRow>
            )}
            {state === 'error' && (
              <span className='text-danger' style={{ gridColumn: '1 / -1' }}>
                Не удалось загрузить данные по мэтчам
              </span>
            )}
          </DetailSection>
        </>
      )}

      {chosen && isGroup && (
        <button
          type='button'
          className='btn btn-link btn-sm px-0 mt-2'
          onClick={() => setChosen(null)}
        >
          ← к группе
        </button>
      )}
    </div>
  )
}

export default DepositPopover
